import React from 'react';

/**
 * Rest-timer slider with 30-second magnetic snap points.
 * Range: 5s – 300s (5 min). Snaps to nearest 30s when within 5s.
 * Small manual number-input at the right for exact values.
 */
export default function RestTimerSlider({
  value,
  onChange,
  min = 5,
  max = 300,
  label = 'Rest',
  testIdPrefix = 'rest',
}) {
  const handleSlider = (e) => {
    let v = parseInt(e.target.value, 10);
    // Magnetic snap to nearest 30s if within 5s
    const nearest30 = Math.round(v / 30) * 30;
    if (Math.abs(v - nearest30) <= 5 && nearest30 >= min && nearest30 <= max) {
      v = nearest30;
    }
    onChange(v);
  };

  const format = (secs) => {
    if (secs < 60) return `${secs}s`;
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return s === 0 ? `${m}m` : `${m}m ${s}s`;
  };

  const magnets = [];
  for (let s = 30; s <= 300; s += 30) magnets.push(s);

  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between">
        <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-graphite/60">
          {label}
        </p>
        <div className="flex items-baseline gap-2">
          <input
            type="number"
            min={min}
            max={max}
            step={5}
            value={value}
            onChange={(e) => {
              const v = parseInt(e.target.value, 10);
              if (!isNaN(v)) onChange(Math.max(min, Math.min(max, v)));
            }}
            className="w-16 h-8 px-2 text-right rounded-ios border border-hairline bg-bone text-sm font-black font-heading tabular-nums text-graphite"
            data-testid={`${testIdPrefix}-input`}
          />
          <span className="text-xl font-black font-heading tabular-nums text-userRed min-w-[70px] text-right">
            {format(value)}
          </span>
        </div>
      </div>
      <div className="relative">
        <input
          type="range"
          min={min}
          max={max}
          step={1}
          value={value}
          onChange={handleSlider}
          className="rest-slider"
          data-testid={`${testIdPrefix}-slider`}
        />
        <div className="flex justify-between text-[9px] uppercase tracking-[0.15em] font-bold text-graphite/30 mt-1 tabular-nums">
          <span>5s</span>
          <span>1m</span>
          <span>2m</span>
          <span>3m</span>
          <span>4m</span>
          <span>5m</span>
        </div>
      </div>
      {/* Magnetic markers (thin tick line) */}
      <div className="relative h-1 -mt-4 pointer-events-none">
        <div className="absolute inset-x-0 flex justify-between">
          {magnets.map((m) => {
            const pct = ((m - min) / (max - min)) * 100;
            return (
              <span
                key={m}
                className="absolute w-px h-1 bg-graphite/20"
                style={{ left: `${pct}%` }}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
